import axios from "axios";
import { getSessionToken, refreshSession } from "./sessionRefresh";

const isLocalDashboard = /localhost|127\.0\.0\.1/i.test(
  process.env.VUE_APP_URL || ""
);

const votingApiBaseURL =
  process.env.VUE_APP_VOTING_API_ENDPOINT ||
  (isLocalDashboard
    ? "http://127.0.0.1:3001/voting"
    : "https://api.wechamber.com/voting");

const votingHttp = axios.create({
  baseURL: votingApiBaseURL,
  withCredentials: true,
  timeout: 30000,
});

function isProtectedVotingRequest(config) {
  return String(config && config.url ? config.url : "").includes("/voter/");
}

votingHttp.interceptors.request.use((config) => {
  const token = getSessionToken();
  config.headers = config.headers || {};
  config.headers["X-Requested-With"] = "XMLHttpRequest";
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

votingHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error && error.config;
    const status = error && error.response && error.response.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      !isProtectedVotingRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await refreshSession();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return votingHttp.request(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

function pathSegment(value) {
  return encodeURIComponent(String(value));
}

export function getVotingErrorMessage(error, fallback) {
  const response = error && error.response;
  const message = response && response.data && response.data.message;
  if (typeof message === "string" && message.trim()) return message;

  const nestedMessage =
    response && response.data && response.data.error && response.data.error.message;
  if (typeof nestedMessage === "string" && nestedMessage.trim()) return nestedMessage;

  return fallback;
}

export const votingService = {
  listProcesses() {
    return votingHttp.get("/voter/processes").then((response) => response.data);
  },

  openProcess(electionId) {
    return votingHttp
      .post(`/voter/processes/${pathSegment(electionId)}/access`)
      .then((response) => response.data);
  },

  getBallot(electionId) {
    return votingHttp
      .get(`/voter/processes/${pathSegment(electionId)}/ballot`)
      .then((response) => response.data);
  },

  saveSelections(electionId, selections) {
    return votingHttp
      .patch(`/voter/processes/${pathSegment(electionId)}/ballot/selections`, {
        selections,
      })
      .then((response) => response.data);
  },

  submit(electionId, selections) {
    return votingHttp
      .post(`/voter/processes/${pathSegment(electionId)}/ballot/submit`, {
        selections,
      })
      .then((response) => response.data);
  },

  getPublicResults(shareToken) {
    return votingHttp
      .get(`/public/results/${pathSegment(shareToken)}`)
      .then((response) => response.data);
  },

  verifyPublicResults(shareToken) {
    return votingHttp
      .get(`/public/results/${pathSegment(shareToken)}/verify`)
      .then((response) => response.data);
  },
};
